import { MODEL_VERSION, SITE } from '@/app/constants';
import type { ModelVersion, TaskSettingsV2_0 } from '@/app/types';

interface ResourceAssets {
  url: string;
  mobileVideoSrc?: string;
  poster: string;
  mobilePoster?: string;
}

interface ResourceMeta {
  name: string;
  modelVersion?: ModelVersion;
  settings?: Partial<TaskSettingsV2_0>;
  isClaw?: boolean;
  clawWithReferenceImage?: boolean;
  /** 提供后点击直接跳该路由，跳过 claw / 图生视频逻辑。 */
  href?: string;
}

export type VideoResource = ResourceMeta & ResourceAssets;

interface ResourceConfig extends ResourceMeta {
  china: ResourceAssets;
  global: ResourceAssets;
}

const RESOURCE_CONFIGS: ResourceConfig[] = [
  {
    name: 'heart-hands',
    china: {
      url: 'https://image01.vidu.zone/vidu/media-asset/herovideos1-5239823c.mp4',
      poster: 'https://image01.vidu.zone/vidu/media-asset/herovideos1-b1cbe149.webp',
    },
    global: {
      url: 'https://image01.cf.vidu.studio/vidu/media-asset/herovideos1-5239823c.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/herovideos1-b1cbe149.webp',
    },
    href: '/vidu-stream',
  },
  {
    name: 'helmet',
    china: {
      url: 'https://image01.vidu.zone/vidu/media-asset/herovideos2-4e141698.mp4',
      poster: 'https://image01.vidu.zone/vidu/media-asset/herovideos2-694ad93a.webp',
    },
    global: {
      url: 'https://image01.cf.vidu.studio/vidu/media-asset/herovideos2-4e141698.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/herovideos2-694ad93a.webp',
    },
    href: '/vidu-stream',
  },
  {
    name: 'football',
    china: {
      url: 'https://image01.vidu.zone/vidu/media-asset/football-4cb66d3f.mp4',
      poster: 'https://image01.vidu.zone/vidu/media-asset/football-52181e57.webp',
    },
    global: {
      url: 'https://image01.cf.vidu.studio/vidu/media-asset/football-4cb66d3f.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/football-52181e57.webp',
    },
    isClaw: true,
  },
  {
    name: 'mansion',
    china: {
      url: 'https://image01.vidu.zone/vidu/media-asset/mansion-2734e5ab.mp4',
      poster: 'https://image01.vidu.zone/vidu/media-asset/mansion-3a5b5945.webp',
    },
    global: {
      url: 'https://image01.cf.vidu.studio/vidu/media-asset/mansion-2734e5ab.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/mansion-3a5b5945.webp',
    },
    isClaw: true,
  },
  {
    name: 'claw-skateboard',
    china: {
      url: 'https://image01.vidu.zone/vidu/media-asset/hero_claw-82d1f204.mp4',
      poster: 'https://image01.vidu.zone/vidu/media-asset/hero_claw-01ef6461.webp',
    },
    global: {
      url: 'https://image01.cf.vidu.studio/vidu/media-asset/hero_claw-82d1f204.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/hero_claw-01ef6461.webp',
    },
    isClaw: true,
    clawWithReferenceImage: true,
  },
  {
    name: 'closeup-portrait',
    china: {
      url: 'https://image01.vidu.zone/vidu/media-asset/closeup_portrait-c46dafeb.mp4',
      poster: 'https://image01.vidu.zone/vidu/media-asset/closeup_portrait-5a0c0b85.webp',
    },
    global: {
      url: 'https://image01.cf.vidu.studio/vidu/media-asset/closeup_portrait-c46dafeb.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/closeup_portrait-5a0c0b85.webp',
    },
    modelVersion: MODEL_VERSION.Q3,
    settings: {
      movement_amplitude: 'large',
    },
  },
  {
    name: 'dragon',
    china: {
      url: 'https://image01.vidu.zone/vidu/media-asset/dragon-8ea180e8.mp4',
      poster: 'https://image01.vidu.zone/vidu/media-asset/dragon-732eda77.webp',
    },
    global: {
      url: 'https://image01.cf.vidu.studio/vidu/media-asset/dragon-8ea180e8.mp4',
      poster: 'https://image01.cf.vidu.studio/vidu/media-asset/dragon-732eda77.webp',
    },
    modelVersion: MODEL_VERSION.Q3,
    settings: {
      movement_amplitude: 'large',
    },
  },
];

// URL 里含中文（如「比心」「7月7日」）时，未编码字符会让 Next.js 生成的 preload Link 响应头
// 编码失败（header 只能是 ByteString / 0-255）。用 encodeURI 把非 ASCII 字符百分号编码，
// 已编码的 %20 等不受影响（encodeURI 不会转义 %）。
function encodeAssets(assets: ResourceAssets): ResourceAssets {
  return {
    url: encodeURI(assets.url),
    poster: encodeURI(assets.poster),
    ...(assets.mobileVideoSrc ? { mobileVideoSrc: encodeURI(assets.mobileVideoSrc) } : {}),
    ...(assets.mobilePoster ? { mobilePoster: encodeURI(assets.mobilePoster) } : {}),
  };
}

export const VIDEO_RESOURCES: VideoResource[] = RESOURCE_CONFIGS.map(({ china, global, ...rest }) => ({
  ...rest,
  ...encodeAssets(SITE === 'china' ? china : global),
}));
